/**
 * PortalManagementPage — global view of all external portals for this tenant.
 *
 * Shows live data from api.externalViews.list(), grouped by project.
 * Replaces the previous static placeholder.
 *
 * Portal improvements — Jamie Banks request.
 * R3: AI Friday Summary settings + last summary date.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, Users, ExternalLink, Copy, Check, ChevronDown, ChevronUp, Settings, Eye, Sparkles, ToggleLeft, ToggleRight, Mail, MessageSquare } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ViewMember {
  id: string;
  member_email: string;
  role: string;
  status: 'pending' | 'active' | 'revoked';
  created_at: string;
}

interface PortalView {
  id: string;
  name: string;
  token: string;
  is_active: boolean;
  active_member_count?: number;
  pending_member_count?: number;
  last_accessed_at?: string | null;
  welcome_message?: string | null;
  created_at: string;
  project_name?: string | null;
  project_id?: string | null;
}

interface AiSummaryRecord {
  summary_date: string;
  summary_text: string;
  email_sent: boolean;
  created_at: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelativeDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
}

// ── PortalRow ─────────────────────────────────────────────────────────────────

function PortalRow({ view }: { view: PortalView }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const portalUrl = `${window.location.origin}/portal/${view.token}`;
  const previewUrl = `${portalUrl}?preview=true`;
  const activeMemberCount = view.active_member_count ?? 0;
  const pendingMemberCount = view.pending_member_count ?? 0;
  const totalMembers = activeMemberCount + pendingMemberCount;

  // Lazy-load member list when the row is expanded
  const { data: membersData } = useQuery({
    queryKey: ['portal-members', view.id],
    queryFn: () => api.externalViews.listMembers(view.id),
    enabled: expanded,
    staleTime: 60_000,
  });
  const members: ViewMember[] = (membersData?.members ?? []).filter(
    (m: ViewMember) => m.status !== 'revoked'
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      {/* Row header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{view.name}</span>
            {!view.is_active && (
              <Badge variant="outline" className="text-gray-500 text-xs">Inactive</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{portalUrl}</p>
        </div>

        <div className="flex items-center gap-4 shrink-0 text-xs text-muted-foreground">
          <span className="hidden sm:flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {totalMembers > 0 ? (
              <span>
                {activeMemberCount} active
                {pendingMemberCount > 0 && `, ${pendingMemberCount} pending`}
              </span>
            ) : (
              <span>No members</span>
            )}
          </span>
          <span className="hidden md:block">
            Last accessed: {formatRelativeDate(view.last_accessed_at)}
          </span>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={handleCopy}
            title="Copy portal link"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs font-medium hover:bg-muted transition-colors"
            title="Preview portal in a new tab"
          >
            <Eye className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Preview Portal</span>
          </a>
          {view.project_id && (
            <Link
              to={`/projects/${view.project_id}?section=portal`}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs font-medium hover:bg-muted transition-colors"
              title="Manage this portal in the project"
            >
              <Settings className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Manage</span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => setExpanded(v => !v)}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t px-4 py-3 space-y-3 bg-muted/20">
          {view.welcome_message && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Welcome message</p>
              <p className="text-sm">{view.welcome_message}</p>
            </div>
          )}

          {/* Member access list */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">
              People with access
            </p>
            {members.length === 0 ? (
              <p className="text-xs text-muted-foreground">No members yet.</p>
            ) : (
              <ul className="space-y-1" data-testid="expanded-members-list">
                {members.map(member => (
                  <li key={member.id} className="flex items-center gap-2 text-sm">
                    <Users className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="truncate">{member.member_email}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      ({member.status})
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Details</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Last accessed: </span>
                <span>{formatRelativeDate(view.last_accessed_at)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Created: </span>
                <span>{formatRelativeDate(view.created_at)}</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Manage members and welcome message from the project's Client Portal section.
          </p>
        </div>
      )}
    </div>
  );
}

// ── PortalViewsList — grouped by project ──────────────────────────────────────

function PortalViewsList({ views }: { views: PortalView[] }) {
  // Group views by project_name; null/undefined -> "Other Portals"
  const grouped = new Map<string, { projectId: string | null | undefined; views: PortalView[] }>();
  const OTHER_KEY = 'Other Portals';

  for (const view of views) {
    const key = view.project_name ?? OTHER_KEY;
    if (!grouped.has(key)) {
      grouped.set(key, { projectId: view.project_id, views: [] });
    }
    grouped.get(key)!.views.push(view);
  }

  // Put "Other Portals" at the end
  const entries = [...grouped.entries()].sort(([a], [b]) => {
    if (a === OTHER_KEY) return 1;
    if (b === OTHER_KEY) return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {views.length} portal{views.length !== 1 ? 's' : ''} active
      </p>
      {entries.map(([projectName, { projectId, views: projectViews }]) => (
        <div key={projectName} className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground border-b pb-1">
            {projectId ? (
              <Link
                to={`/projects/${projectId}`}
                className="hover:text-primary hover:underline transition-colors"
              >
                {projectName}
              </Link>
            ) : (
              projectName
            )}
          </h2>
          <div className="space-y-2">
            {projectViews.map(view => (
              <PortalRow key={view.id} view={view} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── AiSummarySettings ────────────────────────────────────────────────────────

function AiSummarySettings() {
  const queryClient = useQueryClient();

  // Local state for deferred fields (email + prompt save on blur/submit)
  const [emailValue, setEmailValue] = useState('');
  const [promptValue, setPromptValue] = useState('');
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch current settings from tenant_settings
  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['portal-ai-summary-settings'],
    queryFn: () => api.portalAiSummary.getSettings().catch(() => null),
    staleTime: 60_000,
  });

  // Sync local state once settings are loaded (only on first load)
  React.useEffect(() => {
    if (settingsData && !settingsLoaded) {
      setEmailValue(settingsData.email_enabled ? '' : '');
      setPromptValue(settingsData.custom_prompt ?? '');
      setSettingsLoaded(true);
    }
  }, [settingsData, settingsLoaded]);

  const isEnabled: boolean = settingsData?.enabled ?? false;
  const isEmailEnabled: boolean = settingsData?.email_enabled ?? false;

  const updateMutation = useMutation({
    mutationFn: (data: { enabled?: boolean; email_enabled?: boolean; custom_prompt?: string }) =>
      api.portalAiSummary.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-ai-summary-settings'] });
      toast.success('AI summary settings updated');
    },
    onError: () => toast.error('Failed to update settings'),
  });

  // Fetch last summary across all portals
  const { data: summaryData } = useQuery({
    queryKey: ['portal-ai-summaries-latest'],
    queryFn: () => api.portalAiSummary.getLatest().catch(() => null),
    staleTime: 120_000,
  });
  const lastSummary: AiSummaryRecord | null = summaryData?.summary ?? null;

  const handlePromptBlur = () => {
    const saved = settingsData?.custom_prompt ?? '';
    if (promptValue !== saved) {
      updateMutation.mutate({ custom_prompt: promptValue || undefined });
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      {/* Header row */}
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary shrink-0" />
        <h2 className="text-sm font-semibold">AI Friday Summary</h2>
        <span className="text-xs text-muted-foreground ml-auto">Runs every Friday at 4pm PST</span>
      </div>

      <p className="text-xs text-muted-foreground">
        Automatically generates a professional weekly project update and posts it to each portal's message thread.
        Portal members can see the summary without needing to log in.
      </p>

      {/* Enable / disable toggle */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => updateMutation.mutate({ enabled: !isEnabled })}
          disabled={isLoading || updateMutation.isPending}
          className="flex items-center gap-2 text-sm"
          title={isEnabled ? 'Disable AI summary' : 'Enable AI summary'}
        >
          {isEnabled
            ? <ToggleRight className="h-5 w-5 text-primary" />
            : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
          <span className={isEnabled ? 'text-primary font-medium' : 'text-muted-foreground'}>
            {isEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </button>
        {lastSummary && (
          <span className="text-xs text-muted-foreground ml-auto">
            Last summary: {formatRelativeDate(lastSummary.created_at)}
            {lastSummary.email_sent && ' · email sent'}
          </span>
        )}
      </div>

      {/* Email notification toggle */}
      <div className="flex items-center justify-between gap-3 py-2 border-t border-b">
        <div className="flex items-center gap-2">
          <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <div>
            <p className="text-xs font-medium">Email portal members</p>
            <p className="text-xs text-muted-foreground">
              Send each Friday summary to all active portal members
            </p>
          </div>
        </div>
        <button
          onClick={() => updateMutation.mutate({ email_enabled: !isEmailEnabled })}
          disabled={isLoading || updateMutation.isPending}
          className="flex items-center gap-2 text-sm shrink-0"
          title={isEmailEnabled ? 'Disable email notifications' : 'Enable email notifications'}
        >
          {isEmailEnabled
            ? <ToggleRight className="h-5 w-5 text-primary" />
            : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
        </button>
      </div>

      {/* Custom prompt */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <label className="text-xs font-medium" htmlFor="ai-summary-custom-prompt">
            Custom prompt <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
        </div>
        <textarea
          id="ai-summary-custom-prompt"
          value={promptValue}
          onChange={(e) => setPromptValue(e.target.value)}
          onBlur={handlePromptBlur}
          rows={3}
          placeholder="e.g. Focus on construction progress and outstanding items. Use a professional but approachable tone."
          className="w-full rounded-md border bg-background px-3 py-2 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
          disabled={isLoading}
        />
        <p className="text-xs text-muted-foreground">
          Customize the AI prompt used to generate the weekly summary. Leave blank to use the default.
        </p>
      </div>
    </div>
  );
}


// ── PortalManagementPage ──────────────────────────────────────────────────────

export function PortalManagementPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['external-views'],
    queryFn: () => api.externalViews.list(),
    staleTime: 30_000,
  });

  const views: PortalView[] = data?.views ?? [];

  return (
    <DashboardLayout title="Portal Management">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <Globe className="h-7 w-7 text-primary mt-0.5 shrink-0" />
          <div>
            <h1 className="text-2xl font-semibold">Portal Management</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              External portals let your clients, engineers, and contractors see project updates without needing a full account.
              Manage all portals here, or open a project to configure its portal inline.
            </p>
          </div>
        </div>

        {/* AI Summary Settings (R3) */}
        <AiSummarySettings />

        {/* Content */}
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
            <p className="text-sm font-medium text-destructive">Failed to load portals</p>
            <p className="text-sm text-muted-foreground mt-1">
              {(error as Error)?.message || 'Unknown error'}
            </p>
          </div>
        ) : views.length === 0 ? (
          <div className="rounded-lg border border-dashed">
            <EmptyState
              icon={Globe}
              title="No client portals yet"
              description="Share project progress, invoices, and action items with your clients"
              actionLabel="Set Up Portal"
              actionUrl="/projects"
            />
            <div className="text-left max-w-sm mx-auto pb-8 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Getting started</p>
              <ol className="text-sm space-y-1 text-muted-foreground list-decimal list-inside">
                <li>Open any project</li>
                <li>Scroll to the <strong>Client Portal</strong> section</li>
                <li>Enter a client email and click Share</li>
                <li>They'll receive an invitation to view the portal</li>
              </ol>
            </div>
          </div>
        ) : (
          <PortalViewsList views={views} />
        )}
      </div>
    </DashboardLayout>
  );
}
